import { PHRASES, letterAt, type PhraseRef, type PlayerView } from '@il-colpo/engine';
import type { LooseT } from './loose.ts';

/** The player parameters of a phrase are seats; they are shown as names. */
const SEAT_PHRASES = new Set(['corHold', 'suspect', 'trust']);

/** Phrases whose wording depends on one of their parameters (one translation per case). */
const VARIANT_PARAM: Record<string, number> = { panelRule: 1, passClue: 0 };

/** Glyphs of the vault alphabet; they come with names so that colour is never needed. */
export const SYMBOL_GLYPHS = ['♠', '♥', '♦', '♣', '★', '☾', '☀', '⚓'] as const;

export interface PhraseContext {
  /** Translation lookup for the `phrases` namespace. */
  t: LooseT;
  players: PlayerView['players'];
}

/** One translated parameter, ready to be put into the sentence. */
function paramText(ctx: PhraseContext, id: string, phrase: PhraseRef, index: number): string {
  const value = phrase.params[index];
  const spec = (PHRASES as Record<string, { params: readonly { kind: string }[] }>)[id]?.params[
    index
  ];
  if (spec?.kind === 'enum') return ctx.t(`enum.${String(value)}`);
  const n = Number(value);
  if (SEAT_PHRASES.has(id)) return ctx.players.find((p) => p.seat + 1 === n)?.name ?? String(n);
  if (id === 'passClue') {
    return phrase.params[0] === 'category' ? ctx.t(`categories.${n}`) : letterAt(n);
  }
  if (id === 'panelRuleLetter' && index === 2) return letterAt(n);
  if (id === 'panelRuleLetter' && index === 1) return ctx.t(`enum.${String(value)}`);
  if (id === 'vaultPos' && index === 1) {
    return `${SYMBOL_GLYPHS[n] ?? '?'} ${ctx.t(`symbols.${n}`)}`;
  }
  return String(value);
}

/** The sentence a phrase says, in the language of the `phrases` namespace. */
export function phraseText(ctx: PhraseContext, phrase: PhraseRef): string {
  const variant = VARIANT_PARAM[phrase.id];
  const key =
    variant === undefined ? phrase.id : `${phrase.id}_${String(phrase.params[variant] ?? '')}`;
  const values: Record<string, string> = {};
  phrase.params.forEach((_, i) => {
    values[`p${i + 1}`] = paramText(ctx, phrase.id, phrase, i);
  });
  return ctx.t(key, values);
}
